import React, { useCallback } from 'react';
import { View } from 'react-native';
import { rem, Spacer } from 'design-system';
import Text from 'design-system/Text';
import { CIRCLE_SIZE } from 'design-system/LogoBankAccount';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import Amount from 'design-system/Amount';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import { IconBoldAdd } from 'design-system/icons/IconBoldAdd';
import { IconBoldSubtract } from 'design-system/icons/IconBoldSubtract';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import { selectAlpacaAccountClosingDate, selectAlpacaAccountStatus, } from 'features/invest/reducers/selectors';
import { useGetBrokerImage } from 'features/invest/utils/getInvestBrokerImage';
import { setSelectedTradingAccountId } from 'actions/invest';
import { isWeb } from '../../../constants';
/**
 * If you need to show this within a card, use `onCard`, which makes logo icons use darker background in dark mode.
 */
const RowEmmaInvestAccount = ({ data, style, onCard, onPress, disabled, plusIcon = false, minusIcon = false, hideSpaceUserAvatar = false, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const { navigateToTab } = useTabNavigation();
    const myId = useAppSelector(selectUserId);
    const editingSpace = plusIcon || minusIcon;
    const getBrokerImage = useGetBrokerImage();
    const alpacaAccountClosingDate = useAppSelector(selectAlpacaAccountClosingDate);
    const alpacaAccountStatus = useAppSelector(selectAlpacaAccountStatus);
    const isMyAccount = myId === data.userId;
    const handlePress = useCallback(() => {
        if (onPress) {
            onPress();
            return;
        }
        if (!data.userId || !isMyAccount)
            return;
        if (data.balance) {
            dispatch(setSelectedTradingAccountId(data.id));
            navigation.navigate('InvestPortfolio');
            return;
        }
        navigateToTab('Invest');
    }, [onPress, data.userId, data.balance, data.id, isMyAccount]);
    // Web only supports Seccl accounts
    if (isWeb && data.broker === 'alpaca') {
        return null;
    }
    return (<TouchableHighlight hoverOnLight={!editingSpace} onCard underlayColor={colors.background.underlay} activeOpacity={1} onPress={handlePress} disabled={!isMyAccount || disabled}>
      <View style={[styles.rowAccount, style]}>
        {plusIcon ? (<>
            <View style={styles.buttonContainer}>
              <IconBoldAdd color={colors.elements.positive}/>
            </View>
            <Spacer w={16}/>
          </>) : null}

        {minusIcon ? (<>
            <View style={styles.buttonContainer}>
              <IconBoldSubtract color={colors.elements.negative}/>
            </View>
            <Spacer w={16}/>
          </>) : null}
        <View style={[
            styles.container,
            onCard && styles.onCard, // in dark mode, when shown on a card, should have darker background
        ]}>
          <IconEmmaWallet />
        </View>
        <View style={styles.textContainer}>
          <Text Text-16 numberOfLines={1} ellipsizeMode="tail">
            {data.name}
          </Text>
          {!!isMyAccount &&
            data.broker === 'alpaca' &&
            !!alpacaAccountClosingDate && (<Text TextThin-14 Secondary>
                Closing on {alpacaAccountClosingDate}
              </Text>)}
          {!!isMyAccount &&
            alpacaAccountStatus === 'ACTIVE' &&
            data.accountType !== 'ISA' ? (<>
              <Spacer h={4}/>
              {getBrokerImage(data.broker)}
            </>) : undefined}
          {!(hideSpaceUserAvatar || editingSpace) ? (<SpaceUserAvatar xxSmall spaceId={data.spaceId} userId={data.userId}/>) : null}
        </View>
        {!!data.balance && <Amount Numbers-16 value={data.balance}/>}
      </View>
    </TouchableHighlight>);
};
const styleSet = createStyleSheets((colors) => ({
    rowAccount: {
        paddingTop: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
        paddingBottom: rem(16),
    },
    textContainer: {
        flex: 1,
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: rem(16),
    },
    container: {
        alignItems: 'center',
        borderRadius: rem(22),
        width: rem(CIRCLE_SIZE),
        justifyContent: 'center',
        height: rem(CIRCLE_SIZE),
        backgroundColor: colors.cards.onLight,
    },
    onCard: {
        backgroundColor: colors.background.dark,
    },
    buttonContainer: {
        justifyContent: 'center',
    },
}));
export default RowEmmaInvestAccount;
